import { useState } from "react";

import type { AssetLogoTheme } from "~/v2/hooks/useAssetLogoTheme";
import {
  type AssetIconVisualState,
  deriveAssetIconState,
  lightFailedKey,
} from "./assetIcon.derive";

/**
 * Module-level cache of logos whose light (canonical) source has failed to load.
 * Persists for the session so repeated mounts don't retry known-broken URLs.
 */
const failedLogos = new Set<string>();

/**
 * Test-only: clears the module-level failed-logo cache so controller tests can
 * run in isolation without depending on prior test execution order.
 *
 * @internal Do not call in production code.
 */
export function _resetFailedLogosForTest(): void {
  failedLogos.clear();
}

export interface UseAssetIconControllerInput {
  name: string;
  theme: AssetLogoTheme;
  baseUrl: string | undefined;
  isManifestLoading: boolean;
  availableSymbols: ReadonlySet<string> | null;
  symbolsWithDark: ReadonlySet<string> | null;
}

export interface AssetIconControllerValue {
  visualState: AssetIconVisualState;
  initials: string;
  theme: AssetLogoTheme;
  imageSrc: string | undefined;
  imageAlt: string;
  preloadSrc: string | undefined;
  onPreloadLoad: () => void;
  onPreloadError: () => void;
}

export function useAssetIconController(
  input: UseAssetIconControllerInput,
): AssetIconControllerValue {
  const {
    name,
    theme,
    baseUrl,
    isManifestLoading,
    availableSymbols,
    symbolsWithDark,
  } = input;

  const [loaded, setLoaded] = useState(false);
  const [triedFallback, setTriedFallback] = useState(false);
  const [failed, setFailed] = useState(false);

  const {
    visualState: derivedState,
    lightSrc,
    darkSrc,
    hasDarkVariant,
    initials,
  } = deriveAssetIconState({
    name,
    baseUrl,
    isManifestLoading,
    availableSymbols,
    symbolsWithDark,
    failedLightLogos: failedLogos,
  });

  // If the light logo failed in a previous mount the derived state is already
  // "initials"; if it failed in this mount we override here.
  const isRemoteCandidate = derivedState === "remote" && !failed;

  const tryDarkFirst = theme === "dark" && !triedFallback && hasDarkVariant;
  const activeSrc = isRemoteCandidate
    ? tryDarkFirst
      ? darkSrc
      : lightSrc
    : undefined;

  // While waiting for the preload to settle, show the loading skeleton.
  const visualState: AssetIconVisualState =
    derivedState === "loading"
      ? "loading"
      : isRemoteCandidate && !loaded
        ? "loading"
        : isRemoteCandidate && loaded
          ? "remote"
          : "initials";

  const onPreloadLoad = () => {
    setLoaded(true);
  };

  const onPreloadError = () => {
    if (tryDarkFirst) {
      // Dark variant failed — fall back to light.
      setTriedFallback(true);
    } else {
      // Light (canonical) logo failed — cache and show initials.
      failedLogos.add(lightFailedKey(name));
      setFailed(true);
    }
  };

  const preloadSrc =
    isRemoteCandidate && !loaded && activeSrc ? activeSrc : undefined;

  return {
    visualState,
    initials,
    theme,
    imageSrc: loaded ? activeSrc : undefined,
    imageAlt: name,
    preloadSrc,
    onPreloadLoad,
    onPreloadError,
  };
}
